import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ResearchLayout from '../../components/ResearchLayout';
import api from '../../services/api';
import Preloader from '../../components/Preloader';
import { getQuestions } from '../../lib/questionsCache';

const DoctorAnswerQuestions = () => {
  const { user } = useAuth();
  const [pendingAssignments, setPendingAssignments] = useState([]);
  const [completedProtocols, setCompletedProtocols] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [uniqueProtocols, setUniqueProtocols] = useState([]);
  const [filteredProtocols, setFilteredProtocols] = useState([]);
  const [selectedProtocolId, setSelectedProtocolId] = useState(null);
  // State for completed protocols filtering
  const [completedSearchTerm, setCompletedSearchTerm] = useState('');
  const [uniqueCompletedProtocols, setUniqueCompletedProtocols] = useState([]);
  const [filteredCompletedProtocols, setFilteredCompletedProtocols] = useState([]);
  const [selectedCompletedProtocolId, setSelectedCompletedProtocolId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAnswersModal, setShowAnswersModal] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [viewingProtocolName, setViewingProtocolName] = useState('');
  const [viewingCaseId, setViewingCaseId] = useState('');
  const [loadingAnswers, setLoadingAnswers] = useState(false);
  const navigate = useNavigate();

  // Date formatter: mirror DoctorDashboard preference (prefer `assignedAt`) but remain safe.
  // Accepts raw date strings, numeric timestamps (seconds or ms), or objects with common date fields.
  const safeFormatDate = (item) => {
    if (!item && item !== 0) return 'N/A';

    const pickField = (obj) => {
      if (!obj) return null;
      // If obj is a primitive, return it directly
      if (typeof obj === 'string' || typeof obj === 'number') return obj;
      // Common field names to try, in order
      const fields = ['assignedAt', 'assigned_at', 'assigned', 'assigned_on', 'start_time', 'started_at', 'createdAt', 'created_at', 'created', 'updatedAt', 'updated_at', 'timestamp', 'time'];
      for (const f of fields) {
        if (obj[f] !== undefined && obj[f] !== null) return obj[f];
      }
      // Try nested shapes like { assignment: { assigned_at: ... } }
      for (const key of Object.keys(obj)) {
        const val = obj[key];
        if (typeof val === 'object') {
          const nested = pickField(val);
          if (nested) return nested;
        }
      }
      return null;
    };

    let raw = typeof item === 'object' ? pickField(item) : item;
    if (raw === null || raw === undefined) return 'N/A';

    // If numeric string, convert
    if (typeof raw === 'string' && /^\d+$/.test(raw)) {
      raw = Number(raw);
    }

    // If number, decide seconds vs ms
    if (typeof raw === 'number') {
      const n = raw;
      const ms = n > 1e12 ? n : (n > 1e10 ? n : n * 1000); // heuristics
      const d = new Date(ms);
      if (!Number.isNaN(d.getTime())) return d.toLocaleDateString();
      return 'N/A';
    }

    // Try Date parsing for strings
    try {
      const d = new Date(raw);
      if (!Number.isNaN(d.getTime())) return d.toLocaleDateString();
    } catch (e) {
      // fall through
    }
    // Last resort: try Date.parse
    const parsed = Date.parse(String(raw));
    if (!Number.isNaN(parsed)) return new Date(parsed).toLocaleDateString();
    return 'N/A';
  };

  useEffect(() => {
    if (user) {
      loadPendingAssignments();
      loadCompletedProtocols();
    }
  }, [user]);

  // Build a list of unique protocols (only those with unanswered questions)
  useEffect(() => {
    if (!Array.isArray(pendingAssignments)) {
      setUniqueProtocols([]);
      setFilteredProtocols([]);
      return;
    }
    const map = {};
    pendingAssignments.forEach(a => {
      const pid = a.protocol_id || a.protocolId || (a.protocol && (a.protocol.id || a.protocol.protocol_id));
      const pname = a.protocol_name || a.protocolName || (a.protocol && (a.protocol.name || a.protocol.protocol_name)) || 'Unknown Protocol';
      const caseId = a.case_id || a.caseId || (a.case && (a.case.id || a.case.case_id));
      if (!pid) return;
      if (!map[pid]) map[pid] = { protocol_id: pid, protocol_name: pname, case_ids: new Set() };
      if (caseId) map[pid].case_ids.add(String(caseId));
    });
    const protocols = Object.values(map).map(p => ({ ...p, case_ids: Array.from(p.case_ids) }));
    setUniqueProtocols(protocols);
    setFilteredProtocols(protocols);
    // reset selection when pendingAssignments change
    setSelectedProtocolId(null);
  }, [pendingAssignments]);

  // Filter uniqueProtocols by searchTerm (search protocol name or case id)
  useEffect(() => {
    if (!searchTerm) return setFilteredProtocols(uniqueProtocols);
    const q = String(searchTerm).toLowerCase();
    const filtered = uniqueProtocols.filter(p => {
      if ((p.protocol_name || '').toLowerCase().includes(q)) return true;
      if (p.case_ids && p.case_ids.some(cid => String(cid).toLowerCase().includes(q))) return true;
      return false;
    });
    setFilteredProtocols(filtered);
  }, [searchTerm, uniqueProtocols]);

  // Build a list of unique completed protocols
  useEffect(() => {
    if (!Array.isArray(completedProtocols)) {
      setUniqueCompletedProtocols([]);
      setFilteredCompletedProtocols([]);
      return;
    }
    const map = {};
    completedProtocols.forEach(protocol => {
      const pid = protocol.id;
      const pname = protocol.name || 'Unknown Protocol';
      if (!pid) return;
      if (!map[pid]) map[pid] = { protocol_id: pid, protocol_name: pname, case_ids: new Set() };
      protocol.cases.forEach(caseItem => {
        if (caseItem.case_id) map[pid].case_ids.add(String(caseItem.case_id));
      });
    });
    const protocols = Object.values(map).map(p => ({ ...p, case_ids: Array.from(p.case_ids) }));
    setUniqueCompletedProtocols(protocols);
    setFilteredCompletedProtocols(protocols);
    // reset selection when completedProtocols change
    setSelectedCompletedProtocolId(null);
  }, [completedProtocols]);

  // Filter uniqueCompletedProtocols by completedSearchTerm (search protocol name or case id)
  useEffect(() => {
    if (!completedSearchTerm) return setFilteredCompletedProtocols(uniqueCompletedProtocols);
    const q = String(completedSearchTerm).toLowerCase();
    const filtered = uniqueCompletedProtocols.filter(p => {
      if ((p.protocol_name || '').toLowerCase().includes(q)) return true;
      if (p.case_ids && p.case_ids.some(cid => String(cid).toLowerCase().includes(q))) return true;
      return false;
    });
    setFilteredCompletedProtocols(filtered);
  }, [completedSearchTerm, uniqueCompletedProtocols]);

  // Load pending assignments from backend (protocols/cases with pending questions)
  const loadPendingAssignments = async () => {
    try {
      setLoading(true);
  const response = await api.get('/doctors/continue-progress');
  const payload = response && (response.data || response);
  let assignments = [];
  if (Array.isArray(payload)) assignments = payload;
  else if (Array.isArray(payload.assignments)) assignments = payload.assignments;
  else if (Array.isArray(payload.data)) assignments = payload.data;
  setPendingAssignments(assignments || []);
    } catch (err) {
      console.error('Error loading pending assignments:', err);
      setPendingAssignments([]);
    } finally {
      setLoading(false);
    }
  };

  // Load completed protocols/cases for answer review
  const loadCompletedProtocols = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/assignments?doctor_id=${user.id}`);
      const assignmentsData = response.data.assignments || [];
      // Group by protocol/case
      const grouped = assignmentsData.reduce((acc, assignment) => {
        const protocolId = assignment.protocol_id;
        if (!acc[protocolId]) {
          acc[protocolId] = {
            id: protocolId,
            name: assignment.protocol_name || 'Unnamed Protocol',
            cases: [],
          };
        }
        if (!acc[protocolId].cases.find(c => c.case_id === assignment.case_id)) {
          acc[protocolId].cases.push({
            case_id: assignment.case_id,
            case_customer_id: assignment.case_customer_id,
            assignment_id: assignment.id,
            assigned_at: assignment.assigned_at,
          });
        }
        return acc;
      }, {});
      // For each protocol, check if all cases are completed (using answers API)
      const completed = [];
      for (const protocolId in grouped) {
        const protocol = grouped[protocolId];
        let allCasesCompleted = true;
        
        // Use cached questions instead of per-case API call
        const questionsResponse = await getQuestions(protocolId);
        const activeQuestions = questionsResponse.questions || [];
        const totalActiveQuestions = activeQuestions.length;
        
        if (totalActiveQuestions === 0) {
          // If no active questions remain, consider this case completed
          allCasesCompleted = true;
        } else {
          for (const caseItem of protocol.cases) {
            // TODO: Use bulk answers or pass answersByCase as prop for complete N+1 fix
            const answersRes = await api.get(`/answers?protocol_id=${protocolId}&case_id=${caseItem.case_id}`);
            const answers = answersRes.data.answers || [];
            
            // Count answers for active questions only
            const activeQuestionIds = new Set(activeQuestions.map(q => q.id));
            const answersForActiveQuestions = answers.filter(a => activeQuestionIds.has(a.question_id));
            
            if (answersForActiveQuestions.length < totalActiveQuestions) {
              allCasesCompleted = false;
              break;
            }
          }
        }
        if (allCasesCompleted) {
          completed.push(protocol);
        }
      }
      setCompletedProtocols(completed);
    } catch (err) {
      console.error('Error loading completed protocols:', err);
      setCompletedProtocols([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStartQuestions = (protocolId, caseId, batchId) => {
    // Build query parameters - include batch if available for batch-specific workflows
    const params = new URLSearchParams({
      protocol: protocolId,
      case: caseId
    });
    
    // Add batch parameter if available
    if (batchId) {
      params.append('batch', batchId);
    }
    
    navigate(`/doctor/answer-questions/start?${params.toString()}`);
  };

  // View answers modal logic
  const handleViewAnswers = async (protocolId, caseId, protocolName) => {
    try {
      setLoadingAnswers(true);
      setViewingProtocolName(protocolName);
      setViewingCaseId(caseId);
      const response = await api.get(`/answers?case_id=${caseId}&protocol_id=${protocolId}`);
      const answers = response.data.answers || [];
      setSelectedAnswers(answers);
      setShowAnswersModal(true);
    } catch (error) {
      console.error('Error loading answers:', error);
    } finally {
      setLoadingAnswers(false);
    }
  };
  const handleCloseAnswersModal = () => {
    setShowAnswersModal(false);
    setSelectedAnswers([]);
    setViewingProtocolName('');
    setViewingCaseId('');
  };
  const renderAnswersModal = () => {
    if (!showAnswersModal) return null;
    return (
      <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header bg-secondary text-white">
              <h5 className="modal-title">
                <i className="fas fa-clipboard-check me-2"></i>
                Reviews for Case #{viewingCaseId} - {viewingProtocolName}
              </h5>
              <button type="button" className="btn-close btn-close-white" onClick={handleCloseAnswersModal}></button>
            </div>
            <div className="modal-body">
              {loadingAnswers ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading answers...</span>
                  </div>
                </div>
              ) : selectedAnswers.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-bordered table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>Question</th>
                        <th>Your Answer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedAnswers.map(answer => (
                        <tr key={answer.id}>
                          <td>
                            <strong>{answer.question_text}</strong>
                            <div className="small text-muted">Question ID: {answer.question_id}</div>
                          </td>
                          <td>
                            {answer.question_type === 'color_scale' ? (
                              <div className="d-flex align-items-center">
                                <div
                                  className="d-inline-block me-2"
                                  style={{
                                    backgroundColor: answer.answer_text === 'red' ? '#dc3545' :
                                                   answer.answer_text === 'orange' ? '#fd7e14' :
                                                   answer.answer_text === 'yellow' ? '#ffc107' :
                                                   answer.answer_text === 'green' ? '#198754' : '#6c757d',
                                    color: answer.answer_text === 'yellow' ? 'black' : 'white',
                                    padding: '8px 16px',
                                    borderRadius: '5px',
                                    fontWeight: 'bold',
                                    textTransform: 'capitalize'
                                  }}
                                >
                                  {answer.answer_text}
                                </div>
                              </div>
                            ) : (
                              <span className="badge bg-primary">{answer.answer_text}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4">
                  <i className="fas fa-info-circle fa-3x text-muted mb-3"></i>
                  <p>No answers found for this case. If you believe this is an error, please contact support.</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCloseAnswersModal}>
                Close
              </button>
              <button type="button" className="btn btn-primary" onClick={() => window.print()}>
                <i className="fas fa-print me-1"></i>
                Print Answers
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Apply the same protocol filter to completed protocols: only show completed protocols
  // when a protocol is selected to avoid listing thousands of protocols.
  const displayedCompletedProtocols = selectedProtocolId
    ? completedProtocols.filter(p => String(p.id) === String(selectedProtocolId) || String(p.id) === String(selectedProtocolId))
    : [];

  if (loading) {
    return (
      <ResearchLayout pageTitle="Review Cases">
        <Preloader message="Loading questions..." />
      </ResearchLayout>
    );
  }

  return (
    <ResearchLayout pageTitle="Review Cases">
      <div className="container-fluid">
        {/* Protocols with Unanswered Questions (backend-driven) */}
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-dark text-white">
                <h5 className="mb-0">
                  <i className="fas fa-question-circle me-2"></i>
                  Protocols with Unreviewed Cases
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label">Filter protocols by name</label>
                  <div className="input-group">
                    <input
                      type="search"
                      className="form-control"
                      placeholder="Search protocol name"
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                    />
                    <button className="btn btn-outline-secondary" type="button" onClick={() => { setSearchTerm(''); setSelectedProtocolId(null); }}>
                      Clear
                    </button>
                  </div>
                </div>

                {/* Protocol list (only those with unanswered questions) */}
                <div className="mb-3">
                  {filteredProtocols.length === 0 ? (
                    <div className="text-muted">No matching protocols with unanswered questions.</div>
                  ) : (
                    <div className="d-flex flex-wrap">
                      {filteredProtocols.map(p => (
                        <button
                          key={p.protocol_id}
                          className={`btn btn-sm me-2 mb-2 ${selectedProtocolId === p.protocol_id ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setSelectedProtocolId(selectedProtocolId === p.protocol_id ? null : p.protocol_id)}
                        >
                          {p.protocol_name} <span className="badge bg-light text-primary ms-2">{p.case_ids.length}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Show assignments only when a protocol is selected */}
                {selectedProtocolId ? (
                  <div className="row">
                    {pendingAssignments.filter(a => (a.protocol_id || a.protocolId) == selectedProtocolId).map(assignment => (
                      <div key={assignment.assignment_id || `${assignment.protocol_id}-${assignment.case_id}`} className="col-lg-6 col-md-12 mb-4">
                        <div className="card h-100 border-warning">
                          <div className="card-header bg-warning text-dark">
                            <div className="d-flex justify-content-between align-items-center">
                              <h6 className="mb-0 fw-bold">
                                <i className="fas fa-clipboard-list me-2"></i>
                                {assignment.protocol_name || assignment.protocolName}
                              </h6>
                              <span className="badge bg-light text-primary me-2">
                                Case {assignment.case_id}
                              </span>
                            </div>
                          </div>
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <div>
                                <strong>Case ID: {assignment.case_id}</strong>
                                {/* <div className="small text-muted">
                                  Customer ID: {assignment.case_customer_id || 'N/A'}
                                </div> */}
                              </div>
                              <button
                                className="btn btn-primary"
                                onClick={() => handleStartQuestions(assignment.protocol_id, assignment.case_id, assignment.batch_id)}
                              >
                                <i className="fas fa-play me-1"></i>
                                Continue
                              </button>
                            </div>
                            {/* <div className="small text-muted mt-2">
                              <i className="fas fa-calendar me-1"></i>
                              Assigned: {safeFormatDate(assignment)}
                            </div> */}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <div className="text-muted">No protocol selected. Use the filter above to find protocols with unanswered questions.</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Completed Protocols Section (for answer review) */}
        {completedProtocols.length > 0 && (
          <div className="row mt-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-secondary text-white">
                  <h5 className="mb-0">
                    <i className="fas fa-clipboard-check me-2"></i>
                    Completed Protocols
                  </h5>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <label className="form-label">Filter completed protocols by name</label>
                    <div className="input-group">
                      <input
                        type="search"
                        className="form-control"
                        placeholder="Search completed protocol name"
                        value={completedSearchTerm}
                        onChange={e => setCompletedSearchTerm(e.target.value)}
                      />
                      <button className="btn btn-outline-secondary" type="button" onClick={() => { setCompletedSearchTerm(''); setSelectedCompletedProtocolId(null); }}>
                        Clear
                      </button>
                    </div>
                  </div>

                  {/* Completed Protocol list */}
                  <div className="mb-3">
                    {filteredCompletedProtocols.length === 0 ? (
                      <div className="text-muted">No matching completed protocols.</div>
                    ) : (
                      <div className="d-flex flex-wrap">
                        {filteredCompletedProtocols.map(p => (
                          <button
                            key={p.protocol_id}
                            className={`btn btn-sm me-2 mb-2 ${selectedCompletedProtocolId === p.protocol_id ? 'btn-secondary' : 'btn-outline-secondary'}`}
                            onClick={() => setSelectedCompletedProtocolId(selectedCompletedProtocolId === p.protocol_id ? null : p.protocol_id)}
                          >
                            {p.protocol_name} <span className="badge bg-light text-secondary ms-2">{p.case_ids.length}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Show completed cases only when a protocol is selected */}
                  {selectedCompletedProtocolId ? (
                    <div className="row">
                      {completedProtocols
                        .filter(protocol => String(protocol.id) === String(selectedCompletedProtocolId))
                        .flatMap(protocol => protocol.cases.map(caseItem => (
                          <div key={protocol.id + '-' + caseItem.case_id} className="col-lg-6 col-md-12 mb-4">
                            <div className="card h-100 border-secondary">
                              <div className="card-header bg-secondary text-white">
                                <h6 className="mb-0 fw-bold">
                                  <i className="fas fa-clipboard-list me-2"></i>
                                  {protocol.name}
                                </h6>
                              </div>
                              <div className="card-body">
                                <h6 className="mb-3">Completed Case:</h6>
                                <div className="card mb-3 border">
                                  <div className="card-body p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <div>
                                        <strong>Case ID: {caseItem.case_id}</strong>
                                        {/* <div className="small text-muted">
                                          Customer ID: {caseItem.case_customer_id || 'N/A'}
                                        </div> */}
                                      </div>
                                      <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => handleViewAnswers(protocol.id, caseItem.case_id, protocol.name)}
                                      >
                                        <i className="fas fa-clipboard-check me-1"></i>
                                        View Reviews
                                      </button>
                                    </div>
                                    <div className="small text-muted mt-2">
                                      <i className="fas fa-calendar me-1"></i>
                                      Assigned: {safeFormatDate(caseItem)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="text-muted">No protocol selected. Use the filter above to find completed protocols.</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {renderAnswersModal()}
      </div>
    </ResearchLayout>
  );
};

export default DoctorAnswerQuestions;